// Visual Narrative Maker用のユーティリティ関数

import { CSVParseResult } from '../models/narrative.model';

export class NarrativeHelper {
  
  /**
   * ファイルサイズを人間が読みやすい形式にフォーマット
   */
  static formatFileSize(bytes: number | undefined | null): string {
    if (bytes === undefined || bytes === null) {
      return '0 B';
    }

    if (bytes < 1024) {
      return bytes + ' B';
    } else if (bytes < 1024 * 1024) {
      return (bytes / 1024).toFixed(1) + ' KB';
    } else {
      return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    }
  }

  /**
   * CSVファイルの内容をパース
   */
  static parseCSV(csvContent: string): CSVParseResult {
    const result: CSVParseResult = {
      scenes: [],
      errors: []
    };

    const lines = csvContent.split(/\r\n|\n/);
    if (lines.length === 0) {
      result.errors.push('vnm.err.csvEmpty');
      return result;
    }

    const headers = lines[0].split(',').map(h => h.trim());

    if (!headers.includes('prompt')) {
      result.errors.push('vnm.err.csvNoPromptHeader');
      return result;
    }

    const promptIndex = headers.indexOf('prompt');
    const negativePromptIndex = headers.indexOf('negative_prompt');
    const seedIndex = headers.indexOf('seed');

    // データ行をパース
    for (let i = 1; i < lines.length; i++) {
      if (!lines[i].trim()) continue;

      const data = this.parseCSVLine(lines[i]);
      if (!data) {
        result.errors.push(`行 ${i + 1} のパースに失敗しました`);
        continue;
      }

      const prompt = data[promptIndex]?.trim();
      if (!prompt) {
        result.errors.push(`行 ${i + 1} にプロンプトがありません`);
        continue;
      }

      result.scenes.push({
        prompt,
        negativePrompt: negativePromptIndex > -1 ? data[negativePromptIndex]?.trim() : undefined,
        seed: seedIndex > -1 && data[seedIndex] ? parseInt(data[seedIndex], 10) : undefined
      });
    }

    return result;
  }

  /**
   * CSV行をパース（クォート対応）
   */
  private static parseCSVLine(line: string): string[] | null {
    const result: string[] = [];
    let current = '';
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
      const char = line[i];

      if (char === '"' && (i === 0 || line[i - 1] !== '\\')) {
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        result.push(current);
        current = '';
      } else {
        current += char;
      }
    }

    result.push(current);
    return result;
  }

  /**
   * 画像URLをBlobとして取得
   */
  static async fetchImageAsBlob(imageUrl: string): Promise<Blob> {
    const response = await fetch(imageUrl);
    if (!response.ok) {
      throw new Error(`画像の取得に失敗しました: ${response.statusText}`);
    }
    return await response.blob();
  }

  /**
   * 動画URLをBase64として取得
   */
  static async fetchVideoAsBase64(url: string): Promise<string> {
    const response = await fetch(url);
    const blob = await response.blob();
    
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        resolve(reader.result as string);
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }

  /**
   * ユニークなIDを生成
   */
  static generateId(): string {
    return `${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
  }

  /**
   * ファイルが画像かどうかを検証
   */
  static isImageFile(file: File): boolean {
    return file.type.match(/^image\/(jpeg|jpg|png|gif|webp)$/i) !== null;
  }

  /**
   * ファイルサイズが制限内かを検証
   */
  static isFileSizeValid(file: File, maxSizeMB: number = 10): boolean {
    return file.size <= maxSizeMB * 1024 * 1024;
  }

  /**
   * デバウンス関数
   */
  static debounce<T extends (...args: any[]) => void>(
    func: T,
    wait: number
  ): (...args: Parameters<T>) => void {
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    
    return (...args: Parameters<T>) => {
      if (timeoutId !== null) {
        clearTimeout(timeoutId);
      }
      
      timeoutId = setTimeout(() => {
        func(...args);
      }, wait);
    };
  }
}